package ai.beakman.undercover.data

import ai.beakman.undercover.model.WordHint

/**
 * Textos dos temas em espanhol (es).
 *
 * [NAMES]: themeId -> nome do tema. [WORDS]: wordId -> palavra + dica.
 * As chaves são as MESMAS nos três idiomas (ver [ThemeCatalog]).
 *
 * Padrão das dicas: VAGAS, estilo "categoria", curtas, e REPETEM entre 2-3 palavras
 * do mesmo tema (ex.: "Vive en el agua" cobre Delfín+Tiburón). Espelha o agrupamento do _pt.
 */
object ThemeStrings_es {
    val NAMES: Map<String, String> = mapOf(
        "animais" to "Animales",
        "comidas" to "Comidas",
        "profissoes" to "Profesiones",
        "filmes" to "Películas",
        "lugares" to "Lugares",
        "esportes" to "Deportes",
    )

    val WORDS: Map<String, WordHint> = mapOf(
        // animais
        "animais_1" to WordHint("León", "Tiene pelo"),
        "animais_2" to WordHint("Elefante", "Es muy grande"),
        "animais_3" to WordHint("Delfín", "Vive en el agua"),
        "animais_4" to WordHint("Tiburón", "Vive en el agua"),
        "animais_5" to WordHint("Águila", "Se mueve rápido"),
        "animais_6" to WordHint("Caballo", "Anda en cuatro patas"),
        "animais_7" to WordHint("Perro", "Suele estar en casa"),
        "animais_8" to WordHint("Gato", "Suele estar en casa"),
        "animais_9" to WordHint("Panda", "Tiene un color llamativo"),
        "animais_10" to WordHint("Canguro", "Vive muy lejos"),
        "animais_11" to WordHint("Pingüino", "Vive en un clima especial"),
        "animais_12" to WordHint("Jirafa", "Llama la atención por su cuerpo"),

        // comidas
        "comidas_1" to WordHint("Pizza", "Viene de fuera"),
        "comidas_2" to WordHint("Hamburguesa", "Viene de fuera"),
        "comidas_3" to WordHint("Sushi", "Viene de fuera"),
        "comidas_4" to WordHint("Tortilla", "Sabe a casa"),
        "comidas_5" to WordHint("Lasaña", "Aparece en las cenas"),
        "comidas_6" to WordHint("Arepa", "Sabe a casa"),
        "comidas_7" to WordHint("Empanada", "Se come en la calle"),
        "comidas_8" to WordHint("Helado", "Se sirve bien frío"),
        "comidas_9" to WordHint("Brigadeiro", "Aparece en reuniones"),
        "comidas_10" to WordHint("Croqueta", "Se come en la calle"),
        "comidas_11" to WordHint("Asado", "Aparece en reuniones"),
        "comidas_12" to WordHint("Ensalada", "Recuerda a algo ligero"),

        // profissoes
        "profissoes_1" to WordHint("Médico", "Trata con personas"),
        "profissoes_2" to WordHint("Profesor", "Trata con personas"),
        "profissoes_3" to WordHint("Bombero", "Trabajo de riesgo"),
        "profissoes_4" to WordHint("Policía", "Trabajo de riesgo"),
        "profissoes_5" to WordHint("Ingeniero", "Hay que estudiar mucho"),
        "profissoes_6" to WordHint("Abogado", "Hay que estudiar mucho"),
        "profissoes_7" to WordHint("Chef", "Trabaja con las manos"),
        "profissoes_8" to WordHint("Piloto", "Trabaja viajando"),
        "profissoes_9" to WordHint("Dentista", "Trata con personas"),
        "profissoes_10" to WordHint("Periodista", "Trabaja con la información"),
        "profissoes_11" to WordHint("Veterinario", "Trata con seres vivos"),
        "profissoes_12" to WordHint("Arquitecto", "Trabaja creando cosas"),

        // filmes
        "filmes_1" to WordHint("Titanic", "Fue todo un éxito"),
        "filmes_2" to WordHint("Matrix", "Tiene secuela"),
        "filmes_3" to WordHint("Avengers", "Tiene secuela"),
        "filmes_4" to WordHint("Frozen", "Se ve con niños"),
        "filmes_5" to WordHint("El Señor de los Anillos", "Es bastante larga"),
        "filmes_6" to WordHint("Joker", "Fue todo un éxito"),
        "filmes_7" to WordHint("Avatar", "Famosa por sus efectos"),
        "filmes_8" to WordHint("Rápidos y Furiosos", "Tiene secuela"),
        "filmes_9" to WordHint("Harry Potter", "Se ve con niños"),
        "filmes_10" to WordHint("Toy Story", "Se ve con niños"),
        "filmes_11" to WordHint("Jurassic Park", "Famosa por sus efectos"),
        "filmes_12" to WordHint("Rocky", "Tiene secuela"),

        // lugares
        "lugares_1" to WordHint("Playa", "Lugar al aire libre"),
        "lugares_2" to WordHint("Montaña", "Lugar al aire libre"),
        "lugares_3" to WordHint("Bosque", "Lugar al aire libre"),
        "lugares_4" to WordHint("Desierto", "Lugar al aire libre"),
        "lugares_5" to WordHint("Ciudad", "Hay mucha gente"),
        "lugares_6" to WordHint("Escuela", "Se va todos los días"),
        "lugares_7" to WordHint("Hospital", "Nadie quiere quedarse mucho"),
        "lugares_8" to WordHint("Centro comercial", "Hay mucha gente"),
        "lugares_9" to WordHint("Estadio", "Hay mucha gente"),
        "lugares_10" to WordHint("Aeropuerto", "Lugar de paso"),
        "lugares_11" to WordHint("Museo", "Lugar para visitar"),
        "lugares_12" to WordHint("Parque", "Lugar al aire libre"),

        // esportes
        "esportes_1" to WordHint("Fútbol", "Se juega en equipo"),
        "esportes_2" to WordHint("Básquet", "Se juega en equipo"),
        "esportes_3" to WordHint("Vóley", "Se juega en equipo"),
        "esportes_4" to WordHint("Tenis", "Tiene su propio equipo"),
        "esportes_5" to WordHint("Natación", "Exige mucho aliento"),
        "esportes_6" to WordHint("Surf", "Depende del clima"),
        "esportes_7" to WordHint("Skate", "Tiene su propio equipo"),
        "esportes_8" to WordHint("Boxeo", "Tiene contacto físico"),
        "esportes_9" to WordHint("Ciclismo", "Tiene su propio equipo"),
        "esportes_10" to WordHint("Carrera", "Exige mucho aliento"),
        "esportes_11" to WordHint("Golf", "Tiene su propio equipo"),
        "esportes_12" to WordHint("Balonmano", "Se juega en equipo"),
    )
}
